'use client';

import { isEqual } from 'es-toolkit';
import { useState, useEffect } from 'react';
import { getCookie, getStorage } from 'minimal-shared/utils';
import { useCookies, useLocalStorage } from 'minimal-shared/hooks';

import { useColorScheme } from '@mui/material/styles';

import { useRouter, useSearchParams } from 'src/routes/hooks';

import { getIpData } from 'src/locales';
import { getCountryData } from 'src/assets/data';

import { SettingsContext } from './settings-context';
import { SETTINGS_STORAGE_KEY } from '../settings-config';

// ----------------------------------------------------------------------

export function SettingsProvider({
  children,
  cookieSettings,
  defaultSettings,
  storageKey = SETTINGS_STORAGE_KEY,
}) {
  const router = useRouter();

  const { mode } = useColorScheme();

  const searchParams = useSearchParams();

  const isCookieEnabled = !!cookieSettings;
  const useStorage = isCookieEnabled ? useCookies : useLocalStorage;
  const initialSettings = isCookieEnabled ? cookieSettings : defaultSettings;
  const getStorageValue = isCookieEnabled ? getCookie : getStorage;

  const { state, setState, resetState, setField } = useStorage(storageKey, initialSettings);

  const [openDrawer, setOpenDrawer] = useState(false);

  const canReset = !isEqual(state, defaultSettings);

  const onOpenDrawer = () => setOpenDrawer(true);
  const onCloseDrawer = () => setOpenDrawer(false);
  const onReset = () => resetState(defaultSettings);
  const onToggleDrawer = () => setOpenDrawer((prev) => !prev);

  const checkCountry = async () => {
    const country = searchParams.get('country') || state.countryName;
    if (!country) {
      const countryName = await getIpData('name');
      setState({ countryName });
      router.refresh();
    } else if (searchParams.get('country') !== state.countryName) {
      const countryData = getCountryData(country);
      setState({ countryName: countryData?.name ?? '' });
      router.refresh();
    }
  };

  useEffect(() => {
    checkCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, state.countryName]);

  useEffect(() => {
    if (mode) setState({ colorScheme: mode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Version check and reset handling
  useEffect(() => {
    const storedValue = getStorageValue(storageKey);
    if (storedValue) {
      try {
        if (!storedValue.version || storedValue.version !== defaultSettings.version) {
          onReset();
        }
      } catch {
        onReset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValues = {
    canReset,
    onReset,
    openDrawer,
    onOpenDrawer,
    onCloseDrawer,
    onToggleDrawer,
    state,
    setState,
    setField,
  };

  return <SettingsContext value={contextValues}>{children}</SettingsContext>;
}
